import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";

var init = function init() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return arr.slice(0, -1);
};

var last = function last() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return arr.slice(-1)[0];
};
/**
 * sequentialGroupBy groups a list of items based on the result of groupP
 *
 * A good example of this is for palettes, where the grouped things
 * will be a tuple of [ palette, [ ...group ] ]
 *
 * @param {any[]} items A list of items to be sequentially grouped
 * @param {Function} groupP a predicate function that returns `true' if you should create a new group
 * @param {Function} groupF a function to build the group object
 * @returns {any[[][]]} A list of tuples of [GroupCreator]
 */


export var sequentialGroupBy = function sequentialGroupBy() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var groupP = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (i) {
    return i;
  };
  var groupF = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (i) {
    return i;
  };
  return arr.reduce(function (acc, entry, i, initArr) {
    if (groupP(entry, i, initArr)) return [].concat(_toConsumableArray(acc), [[groupF(entry), []]]);
    var front = init(acc);

    var _last = last(acc),
        _last2 = _slicedToArray(_last, 2),
        group = _last2[0],
        items = _last2[1];

    return [].concat(_toConsumableArray(front), [[group, items.concat(entry)]]);
  }, [[null, []]]);
};
export var groupConsecutive = function groupConsecutive() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var consecutiveP = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (a, b) {
    return a === b;
  };
  return arr.reduce(function (acc, it, i, initArr) {
    var front = init(acc);
    var tail = last(acc) || [];
    return consecutiveP(it, initArr[Math.max(i - 1, 0)]) ? [].concat(_toConsumableArray(front), [[].concat(tail).concat(it)]) : [].concat(_toConsumableArray(front), [tail, it]);
  }, []);
};
export var unnest = function unnest() {
  var arr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var unnestP = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (a) {
    return false;
  };
  return arr.reduce(function (acc, cur, i, initArr) {
    var front = init(acc);
    var prev = last(acc) || [];
    if (unnestP(cur)) return [].concat(_toConsumableArray(front), [prev, cur]);
    if (unnestP(prev)) return [].concat(_toConsumableArray(front), [prev, [cur]]);
    return [].concat(_toConsumableArray(front), [[].concat(prev).concat(cur)]);
  }, [[]]).filter(function (item) {
    return !Array.isArray(item) || item.length > 0;
  });
};
/**
 * @typedef {Function} ApplyPredicate
 * @param {Object} prevItem - The previous item in the list to test
 * @param {Object} currItem - The current item in the list to test
 * @returns {any} Returns a truthy value that can be passed into the `ApplyFunction`
 */

/**
 * @typedef {Function} ApplyFunction
 * @param {integer} i The index of the item being modified, i.e. 0 for first, 1 for second
 * @param {Object} item The item to be modified
 * @param {Object} [context] - Extra information to be passed
 * @returns {Object} Returns the modified `item`
 */

/**
 * applyToPairWhen applies a function to pairs within a list based on
 * a predicate
 *
 * A good example of this is for sibling spacing, where we look for
 * pairs of entries in a list of modules and modify them to include
 * some configured spacing
 *
 * @param {any[]} items A list of items to scan over and modify
 * @param {ApplyPredicate} applyP a predicate function that takes the current & previous entries in the list, returns `true' if you should apply `applyF` to them
 * @param {ApplyFunction} applyF a function to modify the entries, it is called with the index of the pair to modify, e.g. 0, 1
 * @returns {[any[],Boolean]} A tuple of the newly modified list of the original `items`, and a boolean of whether it was modified
 **/

export var applyToPairWhen = function applyToPairWhen() {
  var items = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var applyP = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : function (a) {
    return false;
  };
  var applyF = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (_) {
    return _;
  };
  var modded = false;
  return [items.reduce(function (acc, currentModule, i) {
    var frontModules = init(acc);
    var previousModule = last(acc) || {};
    var applyMatch = applyP(previousModule, currentModule, i);

    if (applyMatch) {
      modded = true;
      /*
       * We have a match on the first module, when previous module
       * is undefined so we should only call applyF on the 1th,
       * i.e., currentModule which is the first module in `items`
       */

      if (i === 0) return frontModules.concat([applyF(1, currentModule, applyMatch)]);
      return frontModules.concat([applyF(0, previousModule, applyMatch), applyF(1, currentModule, applyMatch)]);
    }

    return acc.concat(currentModule);
  }, []), modded];
};